import { stripLeadingSeparator } from '@core/utils/formatters';
import type { MyProjectItem } from '@features/projects/types/project.types';
import type { SessionMineItem } from '@features/sessions/types/session.types';
import type { HitField, HitType, SearchHit, TextRange } from '../types/assistant.types';
import { fold, foldTerms, type FoldedText } from './normalize';
import { collapse, toPlainText } from './plain';
import { sharesSkeleton } from './translit';

/** Search over what the browser already has. It outlives the server search:
 *  summaries are encrypted at rest and SQL cannot see them, and «فوز» finding
 *  "Fawz" is a match no trigram will ever make. Server hits merge in on top. */

/** How much a match in each field is worth. A hit in a title is a hit on the
 *  thing itself; a hit in a summary is a hit on something it mentioned. */
const FIELD_WEIGHT: Readonly<Record<HitField, number>> = {
  title: 3,
  description: 1.5,
  summary: 1,
};

const SNIPPET_MAX = 160;
const SNIPPET_LEAD = 48;
const DEFAULT_LIMIT = 20;

interface IndexedField {
  readonly field: HitField;
  /** Display text, already stripped of generator artefacts so that highlight
   *  offsets and rendered text can never disagree. */
  readonly text: string;
  readonly folded: FoldedText;
}

/** What indexing a project actually reads. Widened to a shape rather than a
 *  named row type because the same fields arrive from two places now — the
 *  cached `/me/projects` list and a server search. */
export interface IndexableProject {
  readonly id: number;
  readonly name: string;
  readonly description?: string | null;
  readonly brief_summary?: string | null;
  readonly last_session_at?: string | null;
}

export interface IndexEntry {
  readonly type: HitType;
  readonly id: number;
  readonly projectId: number;
  readonly projectName: string;
  readonly title: string;
  readonly to: string;
  readonly at?: string | null;
  readonly fields: readonly IndexedField[];
}

/** Titles a sitting for display. Supplied by the caller because a missing title
 *  needs a translated fallback, and translation does not belong down here. */
export interface IndexLabels {
  readonly session: (session: SessionMineItem) => string;
}

function indexField(field: HitField, raw: string | null | undefined): IndexedField | null {
  if (!raw) return null;
  // Titles are typed by a person and shown whole; the other two are generated,
  // arrive as Markdown, and are shown in a two-line window with no renderer
  // behind it. `toPlainText` also flattens newlines, so the snippet window is
  // measured over the same single line the reader ends up seeing.
  const text =
    field === 'title'
      ? collapse(raw)
      : toPlainText(field === 'summary' ? stripLeadingSeparator(raw) : raw);
  return text.length > 0 ? { field, text, folded: fold(text) } : null;
}

function fieldsOf(entries: readonly (IndexedField | null)[]): readonly IndexedField[] {
  return entries.filter((entry): entry is IndexedField => entry !== null);
}

export function indexProjects(
  projects: readonly IndexableProject[],
): readonly IndexEntry[] {
  return projects.map<IndexEntry>((project) => ({
    type: 'project',
    id: project.id,
    projectId: project.id,
    projectName: project.name,
    title: project.name,
    to: `/projects/${String(project.id)}`,
    at: project.last_session_at,
    fields: fieldsOf([
      indexField('title', project.name),
      indexField('description', project.description),
      indexField('summary', project.brief_summary),
    ]),
  }));
}

/** Fold the caller's projects and sittings into one searchable list. */
export function buildIndex(
  projects: readonly MyProjectItem[],
  sessions: readonly SessionMineItem[],
  labels: IndexLabels,
): readonly IndexEntry[] {
  const sessionEntries = sessions.map<IndexEntry>((session) => {
    const title = session.title?.trim() ?? '';
    return {
      type: 'session',
      id: session.id,
      projectId: session.project.id,
      projectName: session.project.name,
      title: title.length > 0 ? title : labels.session(session),
      to: `/projects/${String(session.project.id)}/sessions/${String(session.id)}`,
      at: session.ended_at ?? session.started_at,
      fields: fieldsOf([
        indexField('title', title),
        indexField('description', session.description),
        indexField('summary', session.summary),
      ]),
    };
  });

  return [...indexProjects(projects), ...sessionEntries];
}

/** Every occurrence of `term`, in ORIGINAL string coordinates. */
function findRanges(folded: FoldedText, term: string): readonly TextRange[] {
  const ranges: TextRange[] = [];
  let from = 0;

  for (;;) {
    const at = folded.text.indexOf(term, from);
    if (at === -1) break;
    const start = folded.map[at];
    const end = folded.map[at + term.length - 1];
    if (start !== undefined && end !== undefined) ranges.push({ start, end: end + 1 });
    from = at + term.length;
  }

  return ranges;
}

/** True when the match starts a word — "ware" in "warehouse" rather than in
 *  "hardware". Worth more, because that is usually what was meant. */
function startsWord(folded: FoldedText, term: string): boolean {
  const at = folded.text.indexOf(term);
  if (at <= 0) return at === 0;
  return !/[\p{L}\p{N}]/u.test(folded.text.charAt(at - 1));
}

function mergeRanges(ranges: readonly TextRange[]): readonly TextRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: TextRange[] = [];

  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) merged[merged.length - 1] = { start: last.start, end: Math.max(last.end, range.end) };
    else merged.push(range);
  }

  return merged;
}

/** A readable window around the first match, with the highlight offsets moved
 *  into the window's own coordinates. */
function makeSnippet(
  text: string,
  ranges: readonly TextRange[],
): { readonly snippet: string; readonly ranges: readonly TextRange[] } {
  const first = ranges[0]?.start ?? 0;
  let start = Math.max(0, first - SNIPPET_LEAD);
  let end = Math.min(text.length, start + SNIPPET_MAX);

  // Prefer cutting at a space so the window does not open or close mid-word.
  if (start > 0) {
    const space = text.indexOf(' ', start);
    if (space !== -1 && space - start < 20 && space < first) start = space + 1;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > start + 40) end = space;
  }

  let from = start;
  let to = end;
  while (from < to && text[from] === ' ') from += 1;
  while (to > from && text[to - 1] === ' ') to -= 1;

  const prefix = from > 0 ? '…' : '';
  const suffix = to < text.length ? '…' : '';
  const snippet = `${prefix}${text.slice(from, to)}${suffix}`;
  const shift = prefix.length - from;
  const bodyEnd = snippet.length - suffix.length;

  return {
    snippet,
    ranges: ranges
      .filter((range) => range.end > from && range.start < to)
      .map((range) => ({
        start: Math.max(prefix.length, range.start + shift),
        end: Math.min(bodyEnd, range.end + shift),
      })),
  };
}

interface FieldMatch {
  readonly field: IndexedField;
  readonly ranges: readonly TextRange[];
  readonly score: number;
  readonly termCount: number;
}

function matchField(field: IndexedField, terms: readonly string[]): FieldMatch | null {
  const weight = FIELD_WEIGHT[field.field];
  const found: TextRange[] = [];
  let score = 0;
  let termCount = 0;

  for (const term of terms) {
    const ranges = findRanges(field.folded, term);
    if (ranges.length === 0) continue;
    termCount += 1;
    found.push(...ranges);
    score += weight * (startsWord(field.folded, term) ? 2 : 1);
  }

  return termCount === 0 ? null : { field, ranges: mergeRanges(found), score, termCount };
}

/**
 * Rank `entries` against `query`.
 *
 * Every term must appear SOMEWHERE in the entry, but not all in the same field —
 * "warehouse pricing" should find the sitting whose title says warehouse and
 * whose summary discusses pricing, which is exactly the recall a keyword search
 * has to earn to feel like it understood the question.
 */
export function searchIndex(
  entries: readonly IndexEntry[],
  query: string,
  limit: number = DEFAULT_LIMIT,
): readonly SearchHit[] {
  const terms = foldTerms(query);
  if (terms.length === 0) return [];
  const wholeQuery = foldTerms(query).join(' ');

  const hits: SearchHit[] = [];

  for (const entry of entries) {
    const matches = entry.fields
      .map((field) => matchField(field, terms))
      .filter((match): match is FieldMatch => match !== null);
    if (matches.length === 0) continue;

    const covered = new Set<string>();
    for (const match of matches) {
      for (const term of terms) {
        if (match.field.folded.text.includes(term)) covered.add(term);
      }
    }
    if (covered.size < terms.length) continue;

    // The field shown is the one that explains the hit best: most terms first,
    // then heaviest. A title match beats a summary match that merely echoes it.
    const [best] = [...matches].sort((a, b) => b.termCount - a.termCount || b.score - a.score);
    if (!best) continue;
    const titleField = entry.fields.find((field) => field.field === 'title');
    const exactTitle = titleField?.folded.text === wholeQuery;
    const { snippet, ranges } = makeSnippet(best.field.text, best.ranges);

    hits.push({
      type: entry.type,
      id: entry.id,
      projectId: entry.projectId,
      projectName: entry.projectName,
      title: entry.title,
      field: best.field.field,
      snippet,
      ranges,
      to: entry.to,
      at: entry.at,
      score: matches.reduce((sum, match) => sum + match.score, 0) + (exactTitle ? 10 : 0),
    });
  }

  const exact = hits.sort(
    (a, b) => b.score - a.score || (b.at ?? '').localeCompare(a.at ?? ''),
  );

  // One utterance, both scripts. Saying "Fawz" should surface the project called
  // "Fawz" AND one called «فوز» — they are the same name, and which script it was
  // stored in is not something the speaker knows or should have to say. So the
  // cross-script pass always runs; it just never outranks a real match.
  const seen = new Set(exact.map(identity));
  const alsoSpelled = crossScript(entries, query, limit).filter(
    (hit) => !seen.has(identity(hit)),
  );

  return [...exact, ...alsoSpelled].slice(0, limit);
}

/** Two hits are the same thing when they point at the same row. */
function identity(hit: SearchHit): string {
  return `${hit.type}-${String(hit.id)}`;
}

/**
 * The same name, written in the other script. Titles only.
 *
 * An Arabic recogniser hands back «فوز» for a project called "Fawz", and a reader
 * on an Arabic keyboard types the same thing — neither shares a character with the
 * stored name, so this is the difference between voice working in Arabic and voice
 * working in Arabic except for names.
 *
 * Runs on every search, but its hits are appended AFTER the exact ones and never
 * mixed into their ranking: consonant skeletons collide, and a coincidence must
 * not sit above an answer. `sharesSkeleton` also refuses same-script pairs, so
 * this can only ever add rows in the script the searcher did not use.
 */
function crossScript(
  entries: readonly IndexEntry[],
  query: string,
  limit: number,
): readonly SearchHit[] {
  const hits: SearchHit[] = [];

  for (const entry of entries) {
    if (!sharesSkeleton(query, entry.title)) continue;
    hits.push({
      type: entry.type,
      id: entry.id,
      projectId: entry.projectId,
      projectName: entry.projectName,
      title: entry.title,
      field: 'title',
      // No ranges: nothing in the title literally matched what was typed, and
      // highlighting an arbitrary slice would claim otherwise.
      snippet: entry.title,
      ranges: [],
      to: entry.to,
      at: entry.at,
      score: 0,
      approx: true,
    });
  }

  return hits.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? '')).slice(0, limit);
}
